// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { DataViewState, setDataViewLoaded, setDataViewLoading } from "@insite/client-framework/Store/Data/DataState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { UpdateSearchFieldsType } from "@insite/client-framework/Types/UpdateSearchFieldsType";
import { RetailUsersApiParameter, UsersModel, RetailerUserData } from "@synd/App/Services/RetailerManagementServices";
import { Draft } from "immer";

export interface RetailerManagementState extends DataViewState<RetailerUserData> {
    getRetailUsersParameter: RetailUsersApiParameter;
    failedToLoadData: boolean;
    userId?: string;
    isEditMode: boolean;
    isOpen?: boolean;
}

const initialState: RetailerManagementState = {
    getRetailUsersParameter: {
        sort: "RetailerName DESC",
        page: 1,
    },
    isLoading: {},
    byId: {},
    dataViews: {},
    failedToLoadData: false,
    isEditMode: false,
};

const reducer = {
    "RetailerManagement/UpdateSearchFields": (
        draft: Draft<RetailerManagementState>,
        action: { parameter: RetailUsersApiParameter & UpdateSearchFieldsType },
    ) => {
        const { type } = action.parameter;
        delete action.parameter.type;

        if (type === "Replace") {
            draft.getRetailUsersParameter = action.parameter;
        } else if (type === "Initialize") {
            draft.getRetailUsersParameter = {
                ...initialState.getRetailUsersParameter,
                ...action.parameter,
            };
        } else {
            draft.getRetailUsersParameter = {
                ...draft.getRetailUsersParameter,
                ...action.parameter,
            };

            for (const key in draft.getRetailUsersParameter) {
                const value = (<any>draft.getRetailUsersParameter)[key];

                // remove empty parameters
                if (value === "" || value === undefined) {
                    delete (<any>draft.getRetailUsersParameter)[key];
                }
            }

            for (const key in action.parameter) {
                // go back to page 1 if any other parameters changed
                if (
                    draft.getRetailUsersParameter.page &&
                    draft.getRetailUsersParameter.page > 1 &&
                    key !== "page" &&
                    key !== "pageSize"
                ) {
                    draft.getRetailUsersParameter.page = 1;
                }
            }
        }
    },
    "RetailerManagement/BeginLoadData": (
        draft: Draft<RetailerManagementState>,
        action: { parameter: RetailUsersApiParameter },
    ) => {
        setDataViewLoading(draft, action.parameter);
    },
    "RetailerManagement/CompleteLoadData": (
        draft: Draft<RetailerManagementState>,
        action: { parameter: RetailUsersApiParameter; collection: UsersModel },
    ) => {
        setDataViewLoaded(draft, action.parameter, action.collection, collection => collection.users!);
    },
    "RetailerManagement/FailedToLoadData": (draft: Draft<RetailerManagementState>) => {
        draft.isLoading = {};
        draft.failedToLoadData = true;
    },
    "RetailerManagement/OpenCreateEditModal": (
        draft: Draft<RetailerManagementState>,
        action: { parameter: { userId?: string; isEditMode: boolean; isOpen: boolean } },
    ) => {
        draft.userId = action.parameter.userId;
        draft.isEditMode = action.parameter.isEditMode;
        draft.isOpen = action.parameter.isOpen;
    },
    "RetailerManagement/Reset": () => {
        return initialState;
    },
};

const xRetailerManagement = createTypedReducerWithImmer(initialState, reducer);

(reducers as any).xRetailerManagement = xRetailerManagement;

export type xRetailerManagementState = {
    xRetailerManagement: RetailerManagementState;
} & ApplicationState;

type xRetailerManagementReducers = {
    RetailerManagement: typeof xRetailerManagement;
};

export type xRetailerManagementActions =
    | Parameters<xRetailerManagementReducers[keyof xRetailerManagementReducers]>[1]
    | AnyAction;
